// Stable className strings backed by globally-injected CSS. Preserves the
// `useStyles().<key>` consumer API (form/index.js + room-select + trade-select)
// without @mui/styles.
import { alpha, useTheme } from '@mui/material/styles';

const MWIDTH = 320;
const PREFIX = 'globalForm';

export const Symbols = {
  icon: {
    width: 24,
    height: 24,
    fontSize: 24,
  },
};

const classes = {
  row: `${PREFIX}-row`,
  centerColumn: `${PREFIX}-centerColumn`,
  formContainer: `${PREFIX}-formContainer`,
  formContainerNoMargin: `${PREFIX}-formContainerNoMargin`,
  formContainerFullWidth: `${PREFIX}-formContainerFullWidth`,
  formWrap: `${PREFIX}-formWrap`,
  formWrapHover: `${PREFIX}-formWrapHover`,
  realmsText: `${PREFIX}-realmsText`,
  canClick: `${PREFIX}-canClick`,
  canClickDisabled: `${PREFIX}-canClickDisabled`,
  formControl: `${PREFIX}-formControl`,
  formInputLabel: `${PREFIX}-formInputLabel`,
  titleContainer: `${PREFIX}-titleContainer`,
  title: `${PREFIX}-title`,
  titleRow: `${PREFIX}-titleRow`,
  screenContainer: `${PREFIX}-screenContainer`,
  colorSliderWrap: `${PREFIX}-colorSliderWrap`,
  screenSubContainer: `${PREFIX}-screenSubContainer`,
  screenSubContainerDialog: `${PREFIX}-screenSubContainerDialog`,
  screenWrapper: `${PREFIX}-screenWrapper`,
  collapsedScreen: `${PREFIX}-collapsedScreen`,
  collapseButton: `${PREFIX}-collapseButton`,
  inputEach: `${PREFIX}-inputEach`,
  inputDisabled: `${PREFIX}-inputDisabled`,
  inputDisabledLast: `${PREFIX}-inputDisabledLast`,
  itemRoot: `${PREFIX}-itemRoot`,
  dropZone: `${PREFIX}-dropZone`,
  dropZoneError: `${PREFIX}-dropZoneError`,
  imageWrap: `${PREFIX}-imageWrap`,
  comment: `${PREFIX}-comment`,
  width100: `${PREFIX}-width100`,
  multiSelectChipContainer: `${PREFIX}-multiSelectChipContainer`,
  multiSelectChipWrap: `${PREFIX}-multiSelectChipWrap`,
  multiSelectChip: `${PREFIX}-multiSelectChip`,
  userSelectChip: `${PREFIX}-userSelectChip`,
  toggleBtnRoot: `${PREFIX}-toggleBtnRoot`,
  toggleListRoot: `${PREFIX}-toggleListRoot`,
  topBottomSpacing: `${PREFIX}-topBottomSpacing`,
  toggleFormControl: `${PREFIX}-toggleFormControl`,
  richtextlabel: `${PREFIX}-richtextlabel`,
  richtextlabeltop: `${PREFIX}-richtextlabeltop`,
  richTextDisabled: `${PREFIX}-richTextDisabled`,
  formControlCenter: `${PREFIX}-formControlCenter`,
  labelCenter: `${PREFIX}-labelCenter`,
  ourLabel: `${PREFIX}-ourLabel`,
  infoIcon: `${PREFIX}-infoIcon`,
  addBtn: `${PREFIX}-addBtn`,
  removeBtn: `${PREFIX}-removeBtn`,
  positionContainer: `${PREFIX}-positionContainer`,
  positionWrap: `${PREFIX}-positionWrap`,
  posNumber: `${PREFIX}-posNumber`,
  backgroundPrimary: `${PREFIX}-backgroundPrimary`,
  posTitle: `${PREFIX}-posTitle`,
  colorPrimary: `${PREFIX}-colorPrimary`,
  bar: `${PREFIX}-bar`,
  buttonsContainer: `${PREFIX}-buttonsContainer`,
  button: `${PREFIX}-button`,
  buttonFinish: `${PREFIX}-buttonFinish`,
  squareColor: `${PREFIX}-squareColor`,
  chipAddChipContainer: `${PREFIX}-chipAddChipContainer`,
  colorPickerOverlay: `${PREFIX}-colorPickerOverlay`,
  colorPicker: `${PREFIX}-colorPicker`,
  initialFiles: `${PREFIX}-initialFiles`,
  primaryColor: `${PREFIX}-primaryColor`,
};

// Re-inject when the theme tokens we depend on change, so account-settings
// overrides propagate to className-based consumers.
let injectedHash = null;
const themeHash = (theme) =>
  [
    theme.palette.mode,
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.background.paper,
    theme.palette.grey?.['200'],
    theme.palette.grey?.['400'],
    theme.palette.grey?.['500'],
    theme.breakpoints?.values?.sm,
    theme.breakpoints?.values?.md,
  ].join('|');

const injectStyles = (theme) => {
  if (typeof document === 'undefined') return;
  const hash = themeHash(theme);
  if (injectedHash === hash) return;
  const prev = document.getElementById(`${PREFIX}-styles`);
  if (prev) prev.remove();
  injectedHash = hash;

  const css = `
    .${PREFIX}-row {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      width: 100%;
    }
    .${PREFIX}-row .inputEach { margin-left: ${theme.spacing(0.5)}; margin-right: ${theme.spacing(0.5)}; min-width: 0; }
    .${PREFIX}-row .inputEach:first-child { margin-left: 0; }
    .${PREFIX}-row .inputEach:last-child { margin-right: 0; }
    @media (max-width: ${theme.breakpoints.values.sm - 0.05}px) {
      /* Side-by-side inputs (e.g. the create-assessment Visit Start/End dates)
         can't shrink below their min-content width, which overflowed the
         centered row and clipped the left field offscreen on phones
         (PFM-2681). Stack them instead. */
      .${PREFIX}-row { flex-wrap: wrap; }
      .${PREFIX}-row .inputEach { margin-left: 0; margin-right: 0; }
    }

    .${PREFIX}-centerColumn {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin: 1em;
    }

    .${PREFIX}-formScrollContainer {
      width: 100%;
      height: auto;
      overflow-x: hidden;
      overflow-y: auto;
      box-sizing: border-box;
      padding: 0;
    }
    .${PREFIX}-formScrollContainer-showPosition { height: calc(100% - 120px); padding: 0 ${theme.spacing(2)}; }
    .${PREFIX}-formScrollContainer-useHeight { height: 100%; }

    .${PREFIX}-formContainer {
      max-width: ${MWIDTH * 2}px;
      position: relative;
      height: 100%;
      margin: auto;
      display: flex;
    }
    @media (max-width: ${theme.breakpoints.values.md - 0.05}px) {
      .${PREFIX}-formContainer { flex-direction: column; }
    }

    .${PREFIX}-formContainerNoMargin {
      max-width: ${MWIDTH * 2}px;
      position: relative;
      height: 100%;
      display: flex;
    }
    @media (max-width: ${theme.breakpoints.values.md - 0.05}px) {
      .${PREFIX}-formContainerNoMargin { flex-direction: column; }
    }
    .${PREFIX}-formContainerFullWidth { max-width: none; }

    .${PREFIX}-formWrap {
      border: 1px solid ${theme.palette.action.disabled};
      padding: 6px;
      border-radius: 4px;
      width: 100%;
      box-sizing: border-box;
    }
    .${PREFIX}-formWrapHover {
      border: 1px solid ${theme.palette.action.disabled};
      padding: 6px;
      border-radius: 4px;
      width: 100%;
      box-sizing: border-box;
    }
    .${PREFIX}-formWrapHover:hover { border: 1px solid ${theme.palette.action.disabled}; }

    .${PREFIX}-realmsText {
      display: flex;
      flex-direction: row;
      align-items: center;
      position: relative;
      top: -7px;
      width: calc(100% - 30px);
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      color: ${theme.palette.text.primary};
    }
    .${PREFIX}-canClick { cursor: pointer; }
    .${PREFIX}-canClick:hover { border: 1px solid ${theme.palette.action.active}; }
    .${PREFIX}-canClickDisabled { cursor: default; border: none; pointer-events: none; }

    .${PREFIX}-appBar {
      top: auto;
      bottom: 0;
      background-color: transparent;
      box-shadow: none;
    }
    .${PREFIX}-appBar-showPosition { background-color: ${theme.palette.background.paper}; }

    .${PREFIX}-formControl { width: 100%; }
    .${PREFIX}-formInputLabel {
      background-color: ${theme.palette.background.paper};
      padding: 0 8px;
      margin-top: ${theme.spacing(-0.5)};
      margin-left: ${theme.spacing(2)};
    }

    .${PREFIX}-titleContainer {
      max-width: ${MWIDTH}px;
      margin: ${theme.spacing(1)} 0;
      align-self: flex-start;
    }
    .${PREFIX}-title {}
    .${PREFIX}-titleRow {
      display: flex;
      flex-direction: row;
      width: 100%;
      align-items: center;
    }

    .${PREFIX}-screenContainer {
      display: flex;
      flex-direction: column;
      flex: 1;
      width: 100%;
      box-sizing: border-box;
    }
    .${PREFIX}-screenContainer fieldset legend { display: block; }
    .${PREFIX}-screenContainer .MuiToggleButtonGroup-root { background-color: transparent; }

    .${PREFIX}-colorSliderWrap { padding: ${theme.spacing(2)}; }

    .${PREFIX}-screenSubContainer {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .${PREFIX}-screenSubContainer .MuiInputBase-root.Mui-disabled,
    .${PREFIX}-screenSubContainer .MuiInputLabel-formControl {
      -webkit-text-fill-color: ${theme.palette.text.primary};
      color: ${theme.palette.text.primary};
    }
    .${PREFIX}-screenSubContainer .MuiInputBase-root.Mui-disabled input,
    .${PREFIX}-screenSubContainer .MuiInputBase-root.Mui-disabled textarea,
    .${PREFIX}-screenSubContainer .MuiInputBase-root.Mui-disabled .MuiSelect-select,
    .${PREFIX}-screenSubContainer .MuiInputLabel-formControl input,
    .${PREFIX}-screenSubContainer .MuiInputLabel-formControl textarea,
    .${PREFIX}-screenSubContainer .MuiInputLabel-formControl .MuiSelect-select {
      -webkit-text-fill-color: ${theme.palette.text.primary};
      color: ${theme.palette.text.primary};
    }
    .${PREFIX}-screenSubContainer .Mui-disabled.MuiInputBase-formControl .MuiSelect-icon { display: none; }
    .${PREFIX}-screenSubContainer .Mui-disabled.MuiInput-underline:before { border: none !important; }
    .${PREFIX}-screenSubContainer .inputEach { align-items: normal; }

    .${PREFIX}-screenSubContainerDialog {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .${PREFIX}-screenSubContainerDialog .MuiInput-formControl.Mui-disabled,
    .${PREFIX}-screenSubContainerDialog .MuiInputLabel-formControl { color: ${theme.palette.text.primary}; }
    .${PREFIX}-screenSubContainerDialog .Mui-disabled.MuiInputBase-formControl .MuiSelect-icon { display: none; }
    .${PREFIX}-screenSubContainerDialog .Mui-disabled.MuiInput-underline:before { border: none !important; }
    .${PREFIX}-screenSubContainerDialog .inputEach { align-items: normal; }

    .${PREFIX}-screenWrapper {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      width: 100%;
      margin-right: ${theme.spacing(1)};
    }
    @media (max-width: ${theme.breakpoints.values.md - 0.05}px) {
      .${PREFIX}-screenWrapper { width: 100%; margin-right: 0; }
    }

    .${PREFIX}-collapsedScreen { display: none; }
    .${PREFIX}-collapseButton { margin-left: 10px; }
    @media print { .${PREFIX}-collapseButton { display: none; } }

    .${PREFIX}-inputEach {
      padding-top: ${theme.spacing(2)};
      margin-bottom: ${theme.spacing(1.5)};
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
    }
    .${PREFIX}-inputEach .dropArea.ImageSelect { position: absolute; top: 32px; left: 14px; }
    .${PREFIX}-inputEach .MuiFormControl-root label {
      color: ${theme.palette.text.secondary};
      -webkit-text-fill-color: ${theme.palette.text.secondary};
    }

    .${PREFIX}-inputDisabled { border-bottom: 1px solid ${theme.palette.action.disabledBackground}; }
    .${PREFIX}-inputDisabledLast { border-bottom: none; }
    .${PREFIX}-itemRoot { width: 100%; }

    .${PREFIX}-dropZone {
      padding: ${theme.spacing(1)};
      border-radius: 10px;
      width: 100%;
    }
    .${PREFIX}-dropZone .placeholderContent {
      width: 215px;
      height: 263px;
      background-color: ${theme.palette.action.disabled};
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .${PREFIX}-dropZoneError { border-color: ${theme.palette.error.main} !important; }

    .${PREFIX}-imageWrap {
      position: relative;
      margin: ${theme.spacing(1)};
      width: 250px;
      max-width: 100%;
    }
    .${PREFIX}-comment { width: 100%; }
    .${PREFIX}-width100 { width: 100%; }

    .${PREFIX}-multiSelectChipContainer {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      min-height: 32px;
    }
    .${PREFIX}-multiSelectChipWrap { margin: ${theme.spacing(0.5)}; }
    .${PREFIX}-multiSelectChip { margin-right: ${theme.spacing(0.5)}; }

    .${PREFIX}-userSelectChip {
      opacity: 1 !important;
      background-color: transparent;
      font-size: ${theme.typography.body1.fontSize};
      margin: 0px ${theme.spacing(0.5)} 0px 0px !important;
    }
    .${PREFIX}-userSelectChip svg { display: none; }
    .${PREFIX}-userSelectChip .MuiChip-label { padding: 0; }

    .${PREFIX}-toggleBtnRoot { margin: ${theme.spacing(1)} 0; }
    .${PREFIX}-toggleListRoot { margin-left: ${theme.spacing(1)}; }
    .${PREFIX}-topBottomSpacing {
      padding-top: ${theme.spacing(1)};
      padding-bottom: ${theme.spacing(1)};
    }
    .${PREFIX}-toggleFormControl { padding-top: 20px; width: 100%; }
    .${PREFIX}-richtextlabel {
      transform: scale(1);
      position: relative;
      left: 0px !important;
    }
    .${PREFIX}-richtextlabeltop { top: -8px !important; }
    .${PREFIX}-richTextDisabled :first-child { margin-top: ${theme.spacing(2.7)}; }
    .${PREFIX}-richTextDisabled :last-child { margin-bottom: ${theme.spacing(1)}; }

    .${PREFIX}-formControlCenter { align-items: center; justify-content: center; }
    .${PREFIX}-labelCenter { left: auto; }
    .${PREFIX}-ourLabel {
      top: -21px;
      left: -14px;
      font-size: 12px;
      color: ${theme.palette.text.secondary};
      flex-direction: row;
      align-items: center;
      display: flex;
      pointer-events: auto;
    }
    .${PREFIX}-ourLabel .MuiIcon-root { color: ${theme.palette.text.secondary}; }
    .${PREFIX}-infoIcon { font-size: 18px; margin-left: ${theme.spacing(1)}; }
    .${PREFIX}-addBtn { margin-top: 10px; align-self: flex-start; margin-right: 10px; }
    .${PREFIX}-removeBtn { background-color: ${theme.palette.error.main}; }
    .${PREFIX}-removeBtn.MuiButton-contained:hover { background-color: ${alpha(theme.palette.error.main, 0.9)}; }
    .${PREFIX}-positionContainer {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
    .${PREFIX}-positionWrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 50px;
    }
    .${PREFIX}-posNumber {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: ${theme.palette.action.disabled};
      width: 36px;
      height: 36px;
      border-radius: 50%;
      color: ${theme.palette.common.white};
      font-weight: bold;
      margin-bottom: 5px;
    }
    @media (max-width: ${theme.breakpoints.values.xl - 0.05}px) {
      .${PREFIX}-posNumber { width: 30px; height: 30px; }
    }
    .${PREFIX}-backgroundPrimary { background-color: ${theme.palette.primary.main}; }
    .${PREFIX}-posTitle { color: ${theme.palette.action.disabled}; font-size: 12px; }
    .${PREFIX}-colorPrimary { color: ${theme.palette.primary.main}; }
    .${PREFIX}-primaryColor { color: ${theme.palette.primary.main}; }
    .${PREFIX}-bar {
      width: 50px;
      height: 2px;
      background-color: ${theme.palette.action.disabled};
      margin: 0 5px 20px;
    }
    @media (max-width: ${theme.breakpoints.values.xl - 0.05}px) {
      .${PREFIX}-bar { width: 30px; }
    }
    .${PREFIX}-buttonsContainer {
      display: flex;
      flex-direction: row;
      align-items: space-between;
      justify-content: center;
      margin: 15px 0;
    }
    .${PREFIX}-button { margin: 0 5px; }
    .${PREFIX}-buttonFinish { margin: 0 20px; }
    .${PREFIX}-squareColor {
      width: 48px;
      height: 24px;
      border: 1px solid ${theme.palette.divider};
      border-radius: 2px;
      cursor: pointer;
      box-sizing: border-box;
    }
    .${PREFIX}-chipAddChipContainer {
      display: flex;
      flex-direction: column;
      margin-top: ${theme.spacing(1)};
      margin-bottom: ${theme.spacing(1)};
    }
    .${PREFIX}-chipAddChipContainer .MuiChip-root { margin-bottom: ${theme.spacing(1)}; }
    .${PREFIX}-chipAddChipContainer .MuiChip-root .MuiChip-label { width: 100%; }
    .${PREFIX}-colorPickerOverlay {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background-color: transparent;
      z-index: 999;
    }
    .${PREFIX}-colorPicker { position: absolute; top: 36px; right: 0; z-index: 1000; }
    .${PREFIX}-initialFiles {
      margin-top: ${theme.spacing(1.5)};
      margin-bottom: ${theme.spacing(1)};
    }
    .${PREFIX}-initialFiles .MuiChip-root {
      margin-bottom: ${theme.spacing(1)};
      margin-right: ${theme.spacing(1)};
    }
  `;

  const tag = document.createElement('style');
  tag.id = `${PREFIX}-styles`;
  tag.textContent = css;
  document.head.appendChild(tag);
};

export const useStyles = (opts = {}) => {
  const theme = useTheme();
  injectStyles(theme);
  const { showPosition, useHeight } = opts || {};
  // The original makeStyles definition was conditional in this priority order:
  //   showPosition  -> 'calc(100% - 120px)'   (leave room for the bottom tabs)
  //   useHeight     -> '100%'
  //   otherwise     -> 'auto'
  // Emit only the winning class so they don't overwrite each other in CSS order
  // (which was causing the create-assessment page tabs to fall below the
  // visible area — PFM-2599 QA round 1).
  const heightClass = showPosition ? `${PREFIX}-formScrollContainer-showPosition` : useHeight ? `${PREFIX}-formScrollContainer-useHeight` : null;
  return {
    ...classes,
    formScrollContainer: [`${PREFIX}-formScrollContainer`, heightClass].filter(Boolean).join(' '),
    appBar: [`${PREFIX}-appBar`, showPosition && `${PREFIX}-appBar-showPosition`].filter(Boolean).join(' '),
  };
};

export default useStyles;
